/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  createAction,
  createAsyncThunk,
  createSlice,
  isAnyOf,
  PayloadAction,
} from '@reduxjs/toolkit';
import { StoryCard, storyCardApi, StoryCardsUserProfileFeedArgs } from '../services';
import {
  deleteCard,
  editCard,
  eventDelete,
  eventUpdateChannels,
  togglePublishOnCard,
  updateCardEvent,
} from './common';
import { updateStoryCardPosition } from './reducers';

const getStoryCardsUserProfileFeedHandler = async (
  args: StoryCardsUserProfileFeedArgs,
  { dispatch }: any
) => {
  return dispatch(storyCardApi.endpoints.storyCardsUserProfileFeed.initiate({ ...args })).unwrap();
};

export const getStoryCardsUserProfileFeed = createAsyncThunk(
  'storyCardsUserProfileFeed',
  getStoryCardsUserProfileFeedHandler
);

export const loadMoreStoryCardsUserProfileFeed = createAsyncThunk(
  'storyCardsUserProfileFeed/loadMore',
  getStoryCardsUserProfileFeedHandler
);

export const createProfileFeedStoryCard = createAction<StoryCard>('createProfileFeedStoryCard');

interface StoriesCardsUserFeedState {
  isFetched: boolean;
  storyCardsUserProfileFeed: StoryCard[];
  page: number;
  perPage: number;
  hasNextPage: boolean;
}

const initialState: StoriesCardsUserFeedState = {
  isFetched: false,
  storyCardsUserProfileFeed: [],
  page: 0,
  perPage: 0,
  hasNextPage: false,
};

const storyCardsUserProfileFeedSlice = createSlice({
  name: 'storyCardsUserProfileFeed',
  initialState,
  reducers: {
    updateProfileFeedCardPosition: (
      state,
      action: PayloadAction<{ dragId: number; targetId: number }>
    ) => {
      const { dragId, targetId } = action.payload;

      updateStoryCardPosition({ dragId, targetId, feed: state.storyCardsUserProfileFeed });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getStoryCardsUserProfileFeed.fulfilled, (state, action) => {
        const { items, pageInfo } = action.payload;
        const { page, perPage, hasNextPage } = pageInfo;

        state.isFetched = true;
        state.storyCardsUserProfileFeed = items;
        state.page = page;
        state.perPage = perPage;
        state.hasNextPage = hasNextPage;
      })
      .addCase(loadMoreStoryCardsUserProfileFeed.fulfilled, (state, action) => {
        const { items, pageInfo } = action.payload;
        const { page, hasNextPage } = pageInfo;

        state.storyCardsUserProfileFeed.push(...items);
        state.page = page;
        state.hasNextPage = hasNextPage;
      })
      .addCase(createProfileFeedStoryCard, (state, action) => {
        state.storyCardsUserProfileFeed.unshift(action.payload);
      })
      .addCase(editCard, (state, action) => {
        const { id: storyCardId } = action.payload;

        const cardIndex = state.storyCardsUserProfileFeed.findIndex(({ id }) => id === storyCardId);

        if (cardIndex === -1) {
          return;
        }

        state.storyCardsUserProfileFeed[cardIndex] = action.payload;
      })
      .addCase(deleteCard.fulfilled, (state, action) => {
        const { deleted, storyCardId } = action.payload;

        if (!deleted) {
          return;
        }

        state.storyCardsUserProfileFeed = state.storyCardsUserProfileFeed.filter(
          ({ id }) => id !== storyCardId
        );
      })
      .addCase(togglePublishOnCard.fulfilled, (state, action) => {
        const { payload } = action.payload;

        if (!payload) {
          return;
        }

        const { id: storyCardId, status, postingTime } = payload;

        const card = state.storyCardsUserProfileFeed.find(({ id }) => id === storyCardId);

        if (!card) {
          return;
        }

        card.status = status;
        card.postingTime = postingTime;
      })
      .addCase(eventDelete.fulfilled, (state, action) => {
        const { deleted, eventId } = action.payload;

        if (!deleted) {
          return;
        }

        state.storyCardsUserProfileFeed
          .filter((card) => card.eventId === eventId)
          .forEach((card) => {
            card.event = null;
          });
      })
      .addMatcher(
        isAnyOf(updateCardEvent.fulfilled, eventUpdateChannels.fulfilled),
        (state, action) => {
          state.storyCardsUserProfileFeed
            .filter(({ eventId }) => eventId === action.payload.id)
            .forEach((card) => {
              card.event = action.payload;
            });
        }
      );
  },
});

export const { updateProfileFeedCardPosition } = storyCardsUserProfileFeedSlice.actions;
export default storyCardsUserProfileFeedSlice.reducer;
